/**
 * T타입 - 서술형 입력 컴포넌트
 * 손글씨 캔버스 + 이미지 업로드
 */

import React, { useState, useRef } from 'react';
import { PixelButton } from '../common/PixelButton';
import { PixelCanvas, canvasToBlob, clearCanvas } from '../canvas/PixelCanvas';
import { DrawingTools } from '../canvas/DrawingTools';
import axiosInstance from '../../config/axios';
import { useAuthStore } from '../../stores/authStore';

/**
 * EssayInput Props
 */
interface EssayInputProps {
  /** 답안 제출 콜백 (이미지 URL) */
  onSubmit: (imageUrl: string) => void;
  /** 문제 정보 */
  workbookName: string;
  page: number;
  problemNumber: string;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * T타입 서술형 입력 컴포넌트
 *
 * 플로우:
 * 1. 손글씨로 답안 작성
 * 2. 제출 → 이미지 업로드
 * 3. 이미지 URL을 채점 API에 전달 (수동 채점 대기)
 */
export const EssayInput: React.FC<EssayInputProps> = ({
  onSubmit,
  workbookName,
  page,
  problemNumber,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { user } = useAuthStore();

  const [isEraser, setIsEraser] = useState(false);
  const [penWidth, setPenWidth] = useState(3);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * 전체 지우기
   */
  const handleClear = () => {
    if (canvasRef.current) {
      clearCanvas(canvasRef.current);
    }
  };

  /**
   * 답안 제출 (이미지 업로드)
   */
  const handleSubmit = async () => {
    if (!canvasRef.current || !user || disabled) return;

    try {
      setIsUploading(true);

      // 1. Canvas → Blob 변환
      const blob = await canvasToBlob(canvasRef.current);

      // 2. 백엔드 API로 이미지 업로드
      const formData = new FormData();
      formData.append('file', blob, 'answer.png');
      formData.append('workbook_name', workbookName);
      formData.append('page', page.toString());
      formData.append('problem_number', problemNumber);

      const response = await axiosInstance.post('/upload/student-answer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const imageUrl = response.data.url;

      // 3. 이미지 URL 제출
      onSubmit(imageUrl);

      // 4. 제출 후 캔버스 초기화
      setTimeout(() => {
        handleClear();
      }, 500);

    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      alert('답안 제출에 실패했어요 😢 다시 시도해주세요!');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="space-y-2 select-none"
      onDragStart={(e) => e.preventDefault()}
    >
      {/* 안내 메시지 */}
      <p className="text-center font-pixel text-xs text-gray-400">
        서술형 답안 (선생님이 채점)
      </p>

      {/* 드로잉 도구 */}
      <DrawingTools
        isEraser={isEraser}
        onToggleEraser={() => setIsEraser(!isEraser)}
        penWidth={penWidth}
        onPenWidthChange={setPenWidth}
        onClear={handleClear}
      />

      {/* 캔버스 */}
      <div
        className="flex justify-center"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{ touchAction: 'none' }}
      >
        <PixelCanvas
          ref={canvasRef}
          responsive={true}
          smoothing={true}
          penColor="#000000"
          penWidth={penWidth}
          isEraser={isEraser}
        />
      </div>

      {/* 제출 버튼 */}
      <PixelButton
        variant="primary"
        size="small"
        onClick={handleSubmit}
        disabled={disabled || isUploading}
        className="w-full"
      >
        {isUploading ? '업로드 중...' : '제출 (선생님 채점)'}
      </PixelButton>
    </div>
  );
};
